import { useDispatch, useSelector } from 'react-redux';

import { FlowLabels } from '@constants/generic';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { useVerifyBankDetailsMutation } from '@features/authentication/queries/AuthQuery';
import { updateAccountInfo } from '@features/authentication/slices/AuthSlice';
import { getOrganizationId } from '@features/authentication/utils/common';
import { ApiError, FieldError } from '@types';
import { getLocalStorageItem } from '@utils/storage';
import { capitalizeFirstLetter } from '@utils/stringFormat';
import { isInvalidIfscCode } from '@utils/validations';

import { BankInfoType, VerifiedBankInfoType } from '../types';

interface VerifyBankProps {
  bankInfo: BankInfoType;
  nickName?: string;
  referenceId?: string;
  pan?: string;
  setFieldError: (fieldError: FieldError) => void;
  setVerifiedName: (verifiedName: VerifiedBankInfoType) => void;
  setShowConfirmationModal?: (show: boolean) => void;
  setIsVerified?: (isVerified: boolean) => void;
  remarks?: string;
  onVerifyAttempt?: (
    status: 'success' | 'failed',
    details?: {
      verifiedBankName?: string;
      verifiedName?: string;
      failureReason?: string;
    }
  ) => void;
}

const useVerify = () => {
  const dispatch = useDispatch();

  const [verifyBankDetails, { isLoading: isVerifyBankLoading }] =
    useVerifyBankDetailsMutation();

  const userFlow = getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_FLOW);
  const accountInfo = useSelector((state: any) => state.auth.accountInfo);

  const handleVerifyBank = async ({
    bankInfo,
    nickName,
    referenceId,
    pan,
    setFieldError,
    setVerifiedName,
    setShowConfirmationModal,
    setIsVerified,
    remarks,
    onVerifyAttempt
  }: VerifyBankProps) => {
    if (bankInfo.accountNumber !== bankInfo.confirmAccountNumber) {
      setFieldError({
        field: 'accountNumber',
        message: 'Account number mismatch'
      });
      onVerifyAttempt?.('failed', { failureReason: 'Account number mismatch' });
      return;
    }
    if (isInvalidIfscCode(bankInfo.ifsc)) {
      setFieldError({
        field: 'ifsc',
        message: 'Invalid IFSC Code'
      });
      onVerifyAttempt?.('failed', { failureReason: 'Invalid IFSC Code' });
      return;
    }
    if (bankInfo.accountNumber === bankInfo.confirmAccountNumber) {
      const response = await verifyBankDetails({
        userId: getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_ID),
        body: {
          organisation_id: getOrganizationId(userFlow),
          ifsc: bankInfo.ifsc.toUpperCase().trim(),
          account_number: bankInfo.accountNumber.trim(),
          branch_id: undefined,
          nick_name: nickName,
          merchant_reference_id: referenceId,
          pan,
          remarks: remarks || ''
        }
      });
      if ('data' in response) {
        const {
          bank_account_name: bankAccountName,
          bank_address: bankAddress
        } = response?.data || {};
        dispatch(
          updateAccountInfo({
            ...accountInfo,
            verifiedUserName: bankAccountName,
            verifiedBankName: bankAddress,
            accountDetailId: response?.data?.account_detail_id
          })
        );
        setVerifiedName({
          bankName: bankAddress,
          userName: bankAccountName,
          bankAccountNumber: response?.data?.bank_account_number,
          bankIfsc: response?.data?.bank_ifsc,
          bankLogo: response?.data?.bank_logo,
          accountDetailId: response?.data?.account_detail_id
        });
        setIsVerified?.(true);
        setShowConfirmationModal?.(true);

        onVerifyAttempt?.('success', {
          verifiedBankName: bankAddress,
          verifiedName: bankAccountName
        });
        sendMixpanelEvent(Event.BANK_ACCOUNT_VERIFICATION_SUCCESS, {
          verified_name: bankAccountName,
          verified_bank_name: bankAddress,
          flow: FlowLabels[userFlow]
        });
      } else if ('error' in response) {
        const error = response.error as ApiError;
        const errorMessage = error.data?.errors?.length
          ? error.data?.errors[0].message
          : error.data?.message;

        setFieldError({
          field: 'verify',
          message: capitalizeFirstLetter(
            errorMessage || 'Error in verifying bank details'
          )
        });
        setIsVerified?.(false);
        onVerifyAttempt?.('failed', {
          failureReason: errorMessage || 'Error in verifying bank details'
        });
        sendMixpanelEvent(Event.BANK_ACCOUNT_VERIFICATION_ERROR, {
          error: error.data?.errorCode,
          flow: FlowLabels[userFlow]
        });
      }
    }
  };

  return {
    handleVerifyBank,
    isVerifyBankLoading
  };
};

export default useVerify;
